import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import * as authApi from '@/api/auth'
import type { User } from '@/types'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const isAuthenticated = ref(false)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const isAdmin = computed(() => user.value?.role === 'full_access')
  const username = computed(() => user.value?.username || '')

  // Actions
  async function login(username: string, password: string): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      const result = await authApi.login({ username, password })
      
      if (result.success && result.user) {
        user.value = {
          id: result.user.id,
          username: result.user.username,
          email: null,
          role: result.user.role,
          created_at: '',
          last_login: null,
          is_active: true
        }
        isAuthenticated.value = true
        return true
      } else {
        error.value = result.error || 'Login failed'
        return false
      }
    } catch (err) {
      error.value = 'An unexpected error occurred'
      console.error('Login error:', err)
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function logout(): Promise<void> {
    await authApi.logout()
    user.value = null
    isAuthenticated.value = false
    error.value = null
  }

  async function checkAuth(): Promise<void> {
    try {
      const result = await authApi.checkSession()
      
      if (result.authenticated && result.user) {
        user.value = {
          id: result.user.id,
          username: result.user.username,
          email: null,
          role: result.user.role,
          created_at: '',
          last_login: null,
          is_active: true
        }
        isAuthenticated.value = true
      } else {
        user.value = null
        isAuthenticated.value = false
      }
    } catch (err) {
      console.error('Auth check error:', err)
      user.value = null
      isAuthenticated.value = false
    }
  }

  function clearError(): void {
    error.value = null
  }

  return {
    user,
    isAuthenticated,
    isLoading,
    error,
    isAdmin,
    username,
    login,
    logout,
    checkAuth,
    clearError
  }
})
